/**
 * /affiliates/notes — internal notes & tags per publisher.
 *
 * Declares a TanStack Router route and renders `AffiliateNotesPage`. The
 * component is imported by the root-owned `router.tsx` via module
 * augmentation — agents never touch `router.tsx` themselves; the root
 * wire-up cycle picks this route up after the prod merge.
 *
 * Search params reserved for `?id=<affiliate_id>` so operators can deep-link
 * directly to a publisher's notes.
 */
import { createRoute, Link } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { AffiliateNotesPage } from "@/features/affiliate-notes/AffiliateNotesPage";

// ---------------------------------------------------------------------------
// Route — mounts at `/affiliates/notes`.
// ---------------------------------------------------------------------------

interface AffiliateNotesSearch {
	id?: string;
}

export const affiliatesNotesRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/affiliates/notes",
	validateSearch: (raw: Record<string, unknown>): AffiliateNotesSearch => {
		const id = raw.id;
		if (typeof id === "string" && id.trim().length > 0) {
			return { id: id.trim() };
		}
		return {};
	},
	component: AffiliatesNotesRoute,
});

export default affiliatesNotesRoute;

// ---------------------------------------------------------------------------
// Page shell — breadcrumb + heading + the live feature component.
// ---------------------------------------------------------------------------

export function AffiliatesNotesRoute() {
	// Search param `id` reserved for future deep-link wiring; AffiliateNotesPage
	// does not currently accept an `initialAffiliateId` prop.
	void affiliatesNotesRoute.useSearch();

	return (
		<div className="mx-auto flex w-full max-w-5xl flex-col gap-4 px-6 pt-6 sm:pt-10">
			<Breadcrumb />
			<AffiliateNotesPage />
		</div>
	);
}

function Breadcrumb() {
	return (
		<nav aria-label="Breadcrumb" className="text-sm text-zinc-500">
			<ol className="flex flex-wrap items-center gap-1">
				<li>
					<Link
						to="/"
						className="rounded-[6px] px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-900"
					>
						Dashboard
					</Link>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li>
					<span className="rounded-[6px] px-1 py-0.5">Affiliates</span>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li className="text-zinc-900">Notes</li>
			</ol>
		</nav>
	);
}
